import * as showdown from 'showdown'
import { ApckWhatsNewSpinner } from './whats-new-spinner'
import { getLS, setLS } from '../../util/localstorage'
import { delay } from '../../util/helpers'

const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
]

export const ApckWhatsNewModal = {
    components: {
        'apck-whats-new-spinner': ApckWhatsNewSpinner,
    },
    data() {
        return {
            markdownConverter: new showdown.Converter(),
            modalIsOpen: false,
            initialDataLoaded: false,
            activeTab: '',
            activeRelease: '',
            selectedContent: {},
            nextContent: {},
            showContent: true,
            contentFadeInDelay: 0,
            loadingReleases: false,
            tabs: [
                {
                    id: 'unread',
                    label: 'New Releases',
                    data: [],
                },
                {
                    id: 'read',
                    label: 'Previous Releases',
                    data: [],
                },
            ],
            viewedItems: getLS('whatsNewViewedItems', '', true) || [],
        }
    },
    props: {
        fetchUnreadReleases: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        activeReleaseList() {
            const tab = this.getTab(this.activeTab)
            return (
                tab?.data.reduce(
                    (acc, page) => acc.concat(page.items || []),
                    []
                ) || []
            )
        },
        hasActiveReleases() {
            return !!this.activeReleaseList.length > 0
        },
        activeTabClass() {
            return {
                'whats-new__tab-bg': true,
                'whats-new__tab-bg--active': this.activeTab !== 'unread',
            }
        },
        hasAnyItems() {
            return !!this.tabs.some((tab) => tab.data[0]?.items?.length > 0)
        },
        hasUnreadItems() {
            return !!this.getTab('unread').data[0]?.items?.length > 0
        },
        showContentPanel() {
            return !!(this.showContent && this.selectedContent?.content)
        },
    },
    methods: {
        markAsViewed(id) {
            if (!this.viewedItems.includes(id)) this.viewedItems.push(id)
        },
        mainTabId(base) {
            return `${base}-tab`
        },
        releaseTabId(base) {
            return `${base}-release-tab`
        },
        releasePanelId(base) {
            return `${base}-release-panel`
        },
        contentPanelId(base) {
            return `${base}-content-panel`
        },
        setTabIndex(id) {
            return this.activeRelease === id ? 0 : -1
        },
        getTab(tabId) {
            return this.tabs.find((tab) => tab.id === tabId)
        },
        formatDate(isoDate) {
            const dateObj = new Date(isoDate)
            return `${dateObj.getDate()} ${MONTH_NAMES[dateObj.getMonth()]} ${dateObj.getFullYear()}`
        },
        markdown(text) {
            return text ? this.markdownConverter.makeHtml(text) : ''
        },
        tabHandler(tabId, instant = false) {
            if (this.activeTab === tabId) return
            this.activeTab = tabId

            const tab = this.getTab(tabId)
            const firstRelease = tab.data[0]?.items?.[0]
            if (!firstRelease) return

            if (instant) {
                this.selectedContent = firstRelease
                this.activeRelease = firstRelease.id
                this.markAsViewed(firstRelease.id)
                this.contentFadeInDelay = 0
                this.showContent = true
                return
            }

            this.contentFadeInDelay = 300
            this.setSelectedContent(firstRelease)
        },
        tabClass(id) {
            return {
                'whats-new__button': true,
                'whats-new__button--active': id === this.activeTab,
            }
        },
        releaseItemClass(id, tabId) {
            return {
                'whats-new__release-item': true,
                'whats-new__release-item--active': id === this.activeRelease,
                'whats-new__release-item--not-viewed':
                    tabId === 'unread' && !this.viewedItems.includes(id),
            }
        },
        setSelectedContent(release) {
            this.activeRelease = release.id
            this.nextContent = release
            this.showContent = false
            if (this.activeTab === 'unread') this.markAsViewed(release.id)
        },
        async closeModal() {
            this.modalIsOpen = false

            //Mark all unread releases as viewed
            this.viewedItems = this.getTab('unread')?.data.reduce(
                (acc, page) => {
                    const itemIds = page?.items?.map((item) => item.id) || []
                    return acc.concat(itemIds)
                },
                []
            )

            await this.dismissModal()
        },
        async dismissModal() {
            try {
                const response = await fetch(`/whats_new/dismiss`)

                if (!response.ok) {
                    console.error(`Dismiss post failed: ${response.status}`)
                }
            } catch (error) {
                console.error(`Error posting the dismiss date:`, error)
            }
        },
        async initModal() {
            const showWhatsNew = getLS('showWhatsNew') === 'true'

            if (!this.initialDataLoaded && showWhatsNew) {
                // set the flag before we run the async call
                this.initialDataLoaded = true
                try {
                    if (this.fetchUnreadReleases) await this.fetchReleases('unread')
                    await this.fetchReleases('read')
                } catch(e) {
                    this.initialDataLoaded = false
                    throw e
                }
            }

            if (!this.hasAnyItems) {
                this.modalIsOpen = false
                return
            }

            this.modalIsOpen = showWhatsNew

            this.tabHandler(this.hasUnreadItems ? 'unread' : 'read', true)
        },
        async applyContentChange() {
            this.selectedContent = this.nextContent

            await delay(this.contentFadeInDelay)

            this.contentFadeInDelay = 0
            this.showContent = true
        },
        async fetchReleases(type, page = 1) {
            const tab = this.getTab(type)
            if (!tab) return console.warn(`Tab not found: ${type}`)

            // Check the data on this tab - if the page is already present, don't load it again
            const alreadyLoaded = tab.data.some(p => p.pagination?.page === page)
            if (alreadyLoaded) return

            try {
                const response = await fetch(
                    `/whats_new/${type}?page=${page}&per_page=25`
                )

                if (!response.ok) {
                    console.error(`Fetch failed: ${response.status}`)
                    return
                }

                const data = await response.json()

                if (!data.items) {
                    return console.error(`No items in response for ${type}`)
                }

                return tab.data.push(data)
            } catch (error) {
                console.error(`Error fetching ${type} releases:`, error)
            }
        },
        async loadNextPage() {
            const tab = this.getTab(this.activeTab)
            const lastItem = tab.data[tab.data.length - 1]
            const pagination = lastItem.pagination

            if (this.loadingReleases || pagination.page === pagination.pages)
                return

            this.loadingReleases = true

            //TODO:Could probably to remove this delay if the fetch is slow on UAT/Prod
            await delay(200)

            await this.fetchReleases(this.activeTab, pagination.page + 1)
            this.loadingReleases = false
        },
        async scrollHandler(event) {
            const el = event.target

            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
                await this.loadNextPage()
            }
        },
    },
    watch: {
        async modalIsOpen(val) {
            setLS('showWhatsNew', val)

            if (!val) {
                await this.dismissModal()
            }
        },
        viewedItems(val) {
            setLS('whatsNewViewedItems', val)
        },
    },
    mounted: async function () {
        setLS('showWhatsNew', this.fetchUnreadReleases)

        window.addEventListener('storage', this.initModal)

        await this.initModal()
    },
    beforeDestroy() {
        window.removeEventListener('storage', this.initModal)
    },
    template: `
        <div id="vue-whats-new-modal" class="vue-ui-component-wrapper">
            <transition name="whats-new__modal-fade">
                <div class="whats-new" v-if="modalIsOpen">
                    <div class="whats-new__bg"></div>
                    <div class="whats-new__wrapper">
    
                        <!-- SIDEBAR -->
                        <div class="whats-new__sidebar">
                            <h2 class="whats-new__title">What's New?</h2>
    
                            <!-- TABS -->
                            <div v-if="hasUnreadItems" class="whats-new__tabs" role="tablist" aria-label="Release Tabs">
                                <!-- Background slider -->
                                <div :class="activeTabClass"></div>
                                <template v-for="tab in tabs">
                                    <button
                                        v-if="tab.data[0]?.items?.length"
                                        :id="mainTabId(tab.id)"
                                        :key="mainTabId(tab.id)"
                                        :aria-controls="releasePanelId(tab.id)"
                                        :class="tabClass(tab.id)"
                                        @click.prevent="tabHandler(tab.id)"
                                    >
                                        {{ tab.label }}
                                    </button>
                                </template>
                            </div>
                            <h2 v-else class="whats-new__title-h2">Previous Releases</h2>
    
                            <!-- RELEASE LISTS -->
                            <transition name="fade-up" mode="out-in" tag="div" class="whats-new__releases-wrapper">
                                <template v-for="tab in tabs">
                                    <div
                                        v-if="tab.id === activeTab"
                                        @scroll="scrollHandler($event)"
                                        class="whats-new__releases"
                                        role="tabpanel"
                                        :id="releasePanelId(tab.id)"
                                        :key="releasePanelId(tab.id)"
                                        :aria-labelledby="mainTabId(tab.id)"
                                        :tabindex="0"
                                    >
                                        <ul ref="scrollContainer" v-if="hasActiveReleases" class="whats-new__release-list">
                                            <li v-for="release in activeReleaseList" :key="releaseTabId(release.id)">
                                                <button
                                                    :class="releaseItemClass(release.id, tab.id)"
                                                    :id="releaseTabId(tab.id)"
                                                    :role="'tab'"
                                                    :aria-selected="activeRelease === release.id"
                                                    :aria-controls="contentPanelId(release.id)"
                                                    :tabindex="setTabIndex(release.id)"
                                                    @click.prevent="setSelectedContent(release)"
                                                >
                                                    {{ release.title }}
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                </template>
                            </transition>
                            <apck-whats-new-spinner :use-wrapper="false" :show="loadingReleases" title="Loading next page" animation="fade-up" />
                        </div>
    
                        <!-- CONTENT -->
                        <div class="whats-new__content-wrapper">
                            <button class="whats-new__close-button" @click.prevent="closeModal">
                                <i class="fa fa-times"></i>
                            </button>
                            <transition
                                name="fade-up"
                                tag="div"
                                mode="out-in"
                                @after-leave="applyContentChange()"
                            >
                                <div
                                    v-if="showContentPanel"
                                    class="whats-new__content"
                                    role="tabpanel"
                                    :id="contentPanelId(selectedContent.id)"
                                    :aria-labelledby="releaseTabId(selectedContent.id)"
                                    :tabindex="setTabIndex(selectedContent.id)"
                                >
                                    <div class="whats-new__content-header">
                                        <h2 class="whats-new__title whats-new__title--blue">
                                            {{ selectedContent.title }}
                                        </h2>
                                        <p class="whats-new__date">
                                            <i class="fa-regular fa-calendar"></i>
                                            {{ formatDate(selectedContent.published_at) }}
                                        </p>
                                    </div>
                                    <div class="whats-new__content-markdown" v-html="markdown(selectedContent.content)">
                                    </div>
                                </div>
                            </transition>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
    `,
}
